/**
 * The chosen case, open on the desk — the same picture in the same place from
 * the moment it is unlocked (the mission file rising out of it) to the live
 * mission (the clock in its lid, the file, reports and recorder in it).
 */
import type { CSSProperties } from "react";
import type { Tier } from "@/features/funnel/types";
import caseOpenBeginner from "./case-open-beginner.webp";
import caseOpenChallenging from "./case-open-challenging.webp";
import caseOpenImprobable from "./case-open-improbable.webp";

export const OPEN_CASES: Record<Tier, string> = {
  beginner: caseOpenBeginner,
  challenging: caseOpenChallenging,
  improbable: caseOpenImprobable,
};

/** Each case's light, for what glows with it. */
export const CASE_LIGHT: Record<Tier, string> = {
  beginner: "#ffcf3a",
  challenging: "#ff8a3d",
  improbable: "#4fa8ff",
};

/** Where it sits on the desk: close up, right in front of them — on a phone
 *  wider than the screen, its latches running off the sides. */
export const OPEN_CASE_STYLE: CSSProperties = {
  aspectRatio: "1451/1084",
  width: "min(150%, 76dvh, 760px)",
  top: "15%",
  left: "50%",
  translate: "-50% 0",
};

/** The case's front wall, as a clip (drawn again over what lies in the
 *  case, so it sits down inside it). */
export const FRONT_WALL_CLIP = "inset(73.5% 0 0 0)";

/** Placeholder: every mission shows this until its own strike is written
 *  (missionbank.Strike). Remove once the strikes are in. */
const PLACEHOLDER_STRIKE =
  "The Enemy has somehow made it normal to live next to people for years and never learn their names.";

/** A strike's name line (when it has one) and its story, on one line. */
export function parseStrike(story?: string) {
  const text = (story || PLACEHOLDER_STRIKE).trim();
  const [first, ...rest] = text.split("\n");
  // (INCIDENT / OPERATION: the older names, for strikes already written)
  const named = /^(strike|incident|operation)\b/i.test(first.trim());
  const body = (named ? rest : [first, ...rest])
    .map((l) => l.trim())
    .filter(Boolean)
    .join(" ");
  return { name: named ? first.trim() : "", body };
}

type Box = { left: string; top: string; width: string; height: string };
/** The laptop in each case's picture: where its display is, as fractions of
 *  the picture (what shows on it, and the clock, are laid over it). */
export const SCREEN_BOX: Record<Tier, Box> = {
  beginner: { left: "25.7%", top: "15.5%", width: "48.7%", height: "36.8%" },
  challenging: { left: "25.3%", top: "15.3%", width: "49.2%", height: "37.2%" },
  improbable: { left: "25.3%", top: "15.1%", width: "49.4%", height: "37.2%" },
};
/** The laptop's display in this case's picture. */
export const screenFor = (tier: Tier) => SCREEN_BOX[tier];
/** Its centre, as a transform-origin (the case step zooms in on it). */
export const screenCentre = (tier: Tier) => {
  const b = screenFor(tier);
  const mid = (a: string, n: string) => `${parseFloat(a) + parseFloat(n) / 2}%`;
  return `${mid(b.left, b.width)} ${mid(b.top, b.height)}`;
};

/** Each case's colours: Standard keeps the briefing's bright yellow, the
 *  others are muted to sit with the rest of the app. The light they charge,
 *  the day it floods into, and the ink and paper on it. */
export const CASE_PALETTE: Record<Tier, Record<string, string>> = {
  // the backgrounds (day) are light tints of each case's colour; the glow
  // keeps the full colour for accents
  beginner: {
    glow: "#FFD43A",
    flash: "#FFF6C2",
    day: "#FFD43A",
    ink: "#141200",
    paper: "#FFFDF2",
    muted: "#6b6650",
  },
  // one orange throughout: the orb and the button text match the background
  challenging: {
    glow: "#FFA62B",
    flash: "#FFD3A8",
    day: "#FFA62B",
    ink: "#2A1203",
    paper: "#FFF3EA",
    muted: "#7A3F1A",
  },
  improbable: {
    glow: "#5a9fe0",
    flash: "#d6e7f8",
    day: "#5a9fe0",
    ink: "#0e1b2a",
    paper: "#edf3f9",
    muted: "#4f6479",
  },
};

/** A case's palette as CSS custom properties (--glow, --day, --ink, ...). */
export const paletteVars = (tier: Tier) =>
  Object.fromEntries(
    Object.entries(CASE_PALETTE[tier]).map(([k, v]) => [`--${k}`, v]),
  ) as CSSProperties;

/** A mission's tier from its missionbank Level ("CHALLENGING" -> challenging). */
export const tierOfLevel = (level?: string): Tier | undefined =>
  (
    ({
      BEGINNER: "beginner",
      CHALLENGING: "challenging",
      "MISSION IMPROBABLE": "improbable",
    }) as Record<string, Tier | undefined>
  )[(level ?? "").trim().toUpperCase()];
